/**
 * Variance review & approval — `/inventory/counts/$worksheetId/review`.
 *
 * The first and only place the snapshot quantity is revealed: the `review`
 * representation is a separate, permission-gated payload
 * (`inventory.count.review`), and approval is a separate grant again
 * (`inventory.count.approve`) so the person who counted is not necessarily
 * the person who accepts the variance.
 *
 * Approving appends the `count_variance` movements through the ordinary
 * movement service, so it can be refused for exactly the reasons any posting
 * can — a closed period above all — and those refusals are explained here.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle2, XCircle } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  toast,
} from "@erp/ui";

import { ConflictDialog, surfaceProblem } from "@/components/form";
import { formatDateTime } from "@/lib/format";
import { isPreconditionFailed, newIdempotencyKey } from "@/features/shared/ops-api";

import { approveCountWorksheet, rejectCountWorksheet, type CountApprovalResult } from "../api";
import { isZeroDecimal } from "../decimal";
import {
  asInsufficientStockProblem,
  asPeriodClosedProblem,
  isCountIncomplete,
  isInvalidTransition,
  type OccurredAtPeriodClosedProblem,
} from "../problems";
import { inventoryKeys, useCountReview, useWarehouseOptions } from "../queries";
import { AmountText, QuantityText, SignedQuantityText } from "../components/decimal-text";

const COUNT_BADGE_VARIANTS = {
  draft: "outline",
  counting: "warning",
  submitted: "default",
  approved: "success",
  cancelled: "secondary",
} as const;

export interface CountReviewPageProps {
  worksheetId?: string;
  canApprove?: boolean;
  canReadMovements?: boolean;
  onOpenMovements?: (worksheetId: string) => void;
  onBack?: () => void;
}

export function CountReviewPage({
  worksheetId,
  canApprove = false,
  canReadMovements = false,
  onOpenMovements,
  onBack,
}: CountReviewPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [showZeroVariance, setShowZeroVariance] = useState(false);
  const [occurredAt, setOccurredAt] = useState("");
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [periodClosed, setPeriodClosed] = useState<OccurredAtPeriodClosedProblem | null>(null);
  const [approval, setApproval] = useState<CountApprovalResult | null>(null);
  const [conflict, setConflict] = useState(false);

  const detail = useCountReview(worksheetId);
  const worksheet = detail.data?.data ?? null;
  const etag = detail.data?.etag ?? null;
  const warehouses = useWarehouseOptions().data ?? [];

  const lines = useMemo(() => worksheet?.lines ?? [], [worksheet]);
  const varianceLines = useMemo(
    () => lines.filter((line) => !isZeroDecimal(line.variance_qty)),
    [lines],
  );
  const visibleLines = showZeroVariance ? lines : varianceLines;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.counts.all });
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.stock.all });
    void queryClient.invalidateQueries({ queryKey: inventoryKeys.movements.all });
  };

  const explainFailure = (error: unknown): boolean => {
    if (isPreconditionFailed(error)) {
      setConflict(true);
      return true;
    }
    const period = asPeriodClosedProblem(error);
    if (period !== null) {
      setPeriodClosed(period);
      return true;
    }
    if (isInvalidTransition(error)) {
      setTransitionError(
        t(
          "inventory.countReview.invalidTransition",
          "This worksheet is {{status}} — it can no longer be approved or rejected.",
          {
            status: t(
              `inventory.countStatus.${worksheet?.status ?? "draft"}`,
              worksheet?.status ?? "",
            ),
          },
        ),
      );
      return true;
    }
    if (isCountIncomplete(error)) {
      setTransitionError(
        t(
          "inventory.countReview.incomplete",
          "Some lines were never counted. Send it back so the floor can finish.",
        ),
      );
      return true;
    }
    const shortage = asInsufficientStockProblem(error);
    if (shortage !== null) {
      setTransitionError(
        shortage.detail ??
          t(
            "inventory.countReview.insufficientStock",
            "A variance would drive a position negative; the position moved since the snapshot.",
          ),
      );
      return true;
    }
    return false;
  };

  const approveMutation = useMutation({
    mutationFn: () => {
      if (worksheetId === undefined || etag === null) throw new Error("Worksheet is not loaded");
      const body =
        occurredAt === "" ? undefined : { occurred_at: new Date(occurredAt).toISOString() };
      return approveCountWorksheet(worksheetId, body, etag, newIdempotencyKey());
    },
    onSuccess: ({ data }) => {
      setTransitionError(null);
      setPeriodClosed(null);
      setApproval(data);
      toast({
        variant: "success",
        title: t("inventory.countReview.approved", "Worksheet approved"),
      });
      invalidate();
    },
    onError: (error) => {
      if (explainFailure(error)) return;
      surfaceProblem(error, t);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => {
      if (worksheetId === undefined || etag === null) throw new Error("Worksheet is not loaded");
      return rejectCountWorksheet(worksheetId, etag, newIdempotencyKey());
    },
    onSuccess: () => {
      setTransitionError(null);
      toast({
        variant: "success",
        title: t("inventory.countReview.rejected", "Worksheet rejected"),
      });
      invalidate();
    },
    onError: (error) => {
      if (explainFailure(error)) return;
      surfaceProblem(error, t);
    },
  });

  if (detail.isError) {
    // The server refuses the review representation until the sheet has been
    // counted and submitted, and it says so by name. Reporting that as a
    // generic failure sends somebody looking for a fault that isn't there —
    // the sheet is simply not ready, and the way on is to go and count it.
    const incomplete = isCountIncomplete(detail.error);
    return (
      <div className="space-y-3 p-4">
        <p role="alert" className="text-sm text-destructive">
          {incomplete
            ? t(
                "inventory.countReview.notCounted",
                "This sheet has not been counted yet. The variance can only be reviewed once counting is submitted.",
              )
            : t("inventory.countReview.loadError", "The review payload could not be loaded.")}
        </p>
        {incomplete && onBack !== undefined ? (
          <Button type="button" variant="outline" onClick={onBack}>
            {t("inventory.countReview.backToSheet", "Open the count sheet")}
          </Button>
        ) : null}
      </div>
    );
  }

  if (worksheet === null) {
    return (
      <div className="p-4">
        <p className="text-sm text-muted-foreground">{t("inventory.common.loading", "Loading…")}</p>
      </div>
    );
  }

  const warehouse = warehouses.find((candidate) => candidate.id === worksheet.warehouse_id);
  const decidable = worksheet.status === "submitted";

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex flex-wrap items-center gap-2 text-xl font-semibold">
            <span dir="ltr" className="font-mono text-base">
              {worksheet.count_number}
            </span>
            <Badge variant={COUNT_BADGE_VARIANTS[worksheet.status]}>
              {t(`inventory.countStatus.${worksheet.status}`, worksheet.status)}
            </Badge>
          </h1>
          <p className="text-sm text-muted-foreground">
            {warehouse?.code ?? worksheet.warehouse_id} ·{" "}
            {t("inventory.countReview.snapshotAt", "Snapshot {{when}}", {
              when: formatDateTime(worksheet.snapshot_at, i18n.language),
            })}
          </p>
        </div>
        {onBack !== undefined ? (
          <Button type="button" variant="ghost" onClick={onBack}>
            {t("inventory.countEntry.back", "All worksheets")}
          </Button>
        ) : null}
      </div>

      {transitionError !== null ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
        >
          {transitionError}
        </p>
      ) : null}

      {periodClosed !== null ? (
        <div
          role="alert"
          className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
        >
          <p className="font-medium">
            {t("inventory.problems.periodClosedTitle", "That date is in a closed period")}
          </p>
          <p className="text-muted-foreground">
            {t(
              "inventory.problems.periodClosedBody",
              "Period {{period}} is {{state}}. A movement has to be dated inside an open period.",
              {
                period: periodClosed.period,
                state: t(
                  `inventory.periodState.${periodClosed.period_state}`,
                  periodClosed.period_state,
                ),
              },
            )}
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="self-start"
            onClick={() => {
              setOccurredAt("");
              setPeriodClosed(null);
            }}
          >
            {t("inventory.countReview.clearDate", "Post at the server's time instead")}
          </Button>
        </div>
      ) : null}

      {varianceLines.length === 0 ? (
        <p className="rounded-md border border-success/40 bg-success/10 p-3 text-sm">
          {t(
            "inventory.countReview.noVariance",
            "No variances — every counted line matches the snapshot. Approving closes the worksheet without posting anything.",
          )}
        </p>
      ) : null}

      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>
            <h2>{t("inventory.countReview.title", "Variances")}</h2>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Checkbox
              id="count-show-zero"
              checked={showZeroVariance}
              onCheckedChange={(checked) => setShowZeroVariance(checked === true)}
            />
            <Label htmlFor="count-show-zero" className="font-normal">
              {t("inventory.countReview.showZero", "Show matching lines")}
            </Label>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th scope="col" className="p-2 text-start">
                    {t("inventory.common.sku", "SKU")}
                  </th>
                  <th scope="col" className="p-2 text-start">
                    {t("inventory.common.location", "Bin")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("inventory.countReview.snapshotQty", "Expected")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("inventory.countReview.countedQty", "Counted")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("inventory.countReview.varianceQty", "Variance")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("inventory.countReview.varianceValue", "Variance value")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleLines.map((line) => (
                  <tr key={line.id} className="border-b last:border-0">
                    <td className="p-2">
                      <span dir="ltr" className="font-mono text-xs">
                        {line.sku}
                      </span>
                    </td>
                    <td className="p-2">
                      <span dir="ltr" className="font-mono text-xs">
                        {line.location_code}
                      </span>
                    </td>
                    <td className="p-2 text-end">
                      <QuantityText value={line.snapshot_qty} />
                    </td>
                    <td className="p-2 text-end">
                      <QuantityText value={line.counted_qty} />
                    </td>
                    <td className="p-2 text-end">
                      <SignedQuantityText value={line.variance_qty} />
                    </td>
                    <td className="p-2 text-end">
                      <AmountText value={line.variance_value} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {visibleLines.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("inventory.countReview.allMatched", "Nothing to show — every line matched.")}
            </p>
          ) : null}

          {decidable && canApprove ? (
            <div className="flex flex-col gap-3 border-t pt-4">
              <div className="flex max-w-xs flex-col gap-1.5">
                <Label htmlFor="count-approve-at">
                  {t("inventory.countReview.postAt", "Post variances at (optional)")}
                </Label>
                <Input
                  id="count-approve-at"
                  type="datetime-local"
                  value={occurredAt}
                  onChange={(event) => setOccurredAt(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    "inventory.countReview.postAtHint",
                    "Leave empty to post at the server's transaction time.",
                  )}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  loading={approveMutation.isPending}
                  disabled={etag === null || approveMutation.isPending}
                  onClick={() => approveMutation.mutate()}
                >
                  <CheckCircle2 className="size-4" aria-hidden="true" />
                  {t("inventory.countReview.approve", "Approve & post variances")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  loading={rejectMutation.isPending}
                  disabled={etag === null || rejectMutation.isPending}
                  onClick={() => rejectMutation.mutate()}
                >
                  <XCircle className="size-4" aria-hidden="true" />
                  {t("inventory.countReview.reject", "Reject")}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t(
                  "inventory.countReview.rejectHint",
                  "Rejecting closes the worksheet as cancelled; a fresh worksheet is the way to re-count.",
                )}
              </p>
            </div>
          ) : null}

          {!decidable ? (
            <p className="text-sm text-muted-foreground">
              {t(
                "inventory.countReview.notDecidable",
                "Only a submitted worksheet can be approved or rejected.",
              )}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {approval !== null ? (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 p-4 text-sm">
            <span className="font-medium">
              {t("inventory.countReview.postedTitle", "Variances posted")}
            </span>
            <span className="text-muted-foreground">
              {t("inventory.countReview.postedCount", "{{count}} movements appended", {
                count: approval.movement_ids.length,
              })}
            </span>
            {canReadMovements && onOpenMovements !== undefined ? (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0"
                onClick={() => onOpenMovements(worksheet.id)}
              >
                {t("inventory.countReview.viewMovements", "View them in the ledger")}
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <ConflictDialog
        open={conflict}
        onReload={() => {
          setConflict(false);
          void detail.refetch();
        }}
        onDismiss={() => setConflict(false)}
      />
    </div>
  );
}
